import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type Message = {
  id: number;
  thread_id: number;
  sender_id: number;
  sender_role: string;
  direction: string;
  content: string;
  content_type: string;
  is_via_bot: boolean;
  telegram_message_id: number | null;
  created_at: string;
  read_at: string | null;
  // Backend flag: message was drafted by method (LLM-generated). UI currently does not render a marker.
  is_ai_drafted?: boolean;
  // C6 — voice fields. Present when content_type === "voice".
  voice_url?: string | null;
  voice_duration_sec?: number | null;
  transcript_text?: string | null;
};

export type SendMessagePayload =
  | { content: string; content_type?: "text" }
  | {
      content: string;
      content_type: "voice";
      voice_url: string;
      voice_duration_sec: number;
    };

export function useMessages() {
  return useQuery<Message[]>({
    queryKey: ["messages"],
    queryFn: async () => (await api.get<Message[]>("/athlete/messages")).data,
    refetchInterval: 5000,
    staleTime: 0,
    retry: false,
  });
}

export function useUnread() {
  return useQuery<{ unread_count: number }>({
    queryKey: ["unread"],
    queryFn: async () => (await api.get("/athlete/messages/unread")).data,
    // Бейдж непрочитанных — не чат: 5s-опрос со всех страниц давал
    // десятки запросов в минуту. Пуш-события всё равно идут через SSE.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 15_000,
  });
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SendMessagePayload | string) => {
      // Accept legacy plain-string for text messages (every existing
      // call-site uses this) and the discriminated-union shape for
      // voice. Normalise to the API contract here.
      const body =
        typeof payload === "string" ? { content: payload, content_type: "text" } : payload;
      const r = await api.post<Message>("/athlete/messages", body);
      return r.data;
    },
    // PHASE_A_AUDIT SP-2 broader rollout — push the message into the
    // local cache immediately so the bubble appears in chat before the
    // POST round-trip lands. Negative temp id won't collide with real
    // ones; onSettled then invalidates so the server-issued id +
    // server timestamps replace the placeholder.
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: ["messages"] });
      const snapshot = qc.getQueryData<Message[]>(["messages"]);
      const body =
        typeof payload === "string"
          ? { content: payload, content_type: "text" as const }
          : payload;
      const tempMessage: Message = {
        id: -Date.now(),
        thread_id: 0,
        sender_id: 0,
        sender_role: "athlete",
        direction: "out",
        content: body.content,
        content_type: body.content_type ?? "text",
        is_via_bot: false,
        telegram_message_id: null,
        created_at: new Date().toISOString(),
        read_at: null,
        voice_url: "voice_url" in body ? body.voice_url : null,
        voice_duration_sec: "voice_duration_sec" in body ? body.voice_duration_sec : null,
        transcript_text: null,
      };
      qc.setQueryData<Message[]>(["messages"], (prev) =>
        prev ? [...prev, tempMessage] : [tempMessage],
      );
      return { snapshot };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.snapshot !== undefined) {
        qc.setQueryData(["messages"], ctx.snapshot);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["messages"] }),
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post("/athlete/messages/read");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["unread"] }),
  });
}


// ============================================================
// C6 — voice messages: presigned upload intent + uploader helper
// ============================================================

export type VoiceUploadIntent = {
  upload_url: string;
  public_url: string;
  bucket_key: string;
  expires_in_sec: number;
};

/**
 * Request a presigned R2 PUT URL for a voice blob. Server returns 503
 * voice_storage_not_configured when R2 env is not set — caller must
 * surface a "voice messaging offline" banner rather than crash.
 */
export function useVoiceUploadIntent() {
  return useMutation({
    mutationFn: async (input: { ts_iso: string; content_type: string }) => {
      const r = await api.post<VoiceUploadIntent>(
        "/athlete/messages/voice-upload-intent",
        input,
      );
      return r.data;
    },
  });
}

/**
 * Upload a recorded Blob to the presigned R2 URL. Pure fetch — no auth
 * cookies, no app interceptors (R2 uses query-string signature). On
 * failure throws an Error with the HTTP status in the message so the
 * caller can surface a meaningful toast.
 */
export async function uploadVoiceBlob(
  presignedUrl: string,
  blob: Blob,
  contentType: string,
): Promise<void> {
  const r = await fetch(presignedUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: blob,
  });
  if (!r.ok) {
    throw new Error(`voice upload failed: ${r.status} ${r.statusText}`);
  }
}
